/* ─────────────────────────────────────────────────────────────────────
   Tykables 7.0 — Quiz Engine (tyk-quiz.js)

   A modal-based quiz launcher used for:
     • Size finders  — maps answers → recommended size, saves to
                       localStorage (tyk.size.{type}), auto-selects the
                       matching variant on PDPs.
     • Feedback      — configurable questions, submits via Shopify
                       contact form on completion.
     • Recommendation — shows a final content block based on answers.

   HTML entry points:
     • Any element with [data-tyk-quiz="diapers|snappies|denim"] opens a
       built-in size quiz when clicked.
     • Any <tyk-quiz-config> element describes a custom quiz (used by the
       tyk-quiz section for merchant-configured feedback questionnaires).

   Public API:
     window.tykQuiz.open(id)     — open a quiz by id
     window.tykQuiz.getSize(k)   — read saved size for key (diapers|snappies|denim)
     window.tykQuiz.clear(k)     — clear saved size
     window.tykQuiz.register(cfg)— register a custom quiz at runtime
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ── Built-in quizzes ────────────────────────────────────────────────
  // Ranges are inclusive on both ends. For diapers, the user ranges
  // overlap between Medium/Regular/Large; `recommendZones` are the
  // non-overlapping zones the quiz uses to pick a single answer.
  // `chartRange` is the display range shown on the size chart.
  // Inline SVG for the "where do the tapes land" picture choices. Drawn
  // monochrome with currentColor so the tile's text color drives it —
  // blue normally, white when the tile is selected. `leftX` is the left
  // edge of the left tape tab; the right tab is mirrored. Smaller leftX =
  // tapes pulled out toward the sides (running small); larger leftX =
  // tapes meeting near the center (roomier).
  function tapeArtSvg(leftX) {
    var tabW = 22, tabY = 36, tabH = 22, rightX = 120 - leftX - tabW;
    return '' +
      '<svg viewBox="0 0 120 96" role="img" aria-hidden="true" focusable="false">' +
        '<rect x="12" y="10" width="96" height="76" rx="20" fill="currentColor" fill-opacity="0.10" stroke="currentColor" stroke-width="3"/>' +
        '<line x1="60" y1="14" x2="60" y2="82" stroke="currentColor" stroke-width="2" stroke-dasharray="4 5" stroke-opacity="0.4"/>' +
        '<rect x="' + leftX + '" y="' + tabY + '" width="' + tabW + '" height="' + tabH + '" rx="6" fill="currentColor"/>' +
        '<rect x="' + rightX + '" y="' + tabY + '" width="' + tabW + '" height="' + tabH + '" rx="6" fill="currentColor"/>' +
      '</svg>';
  }

  const QUIZZES = {
    diapers: {
      id: 'diapers',
      title: 'Find your diaper size',
      subtitle: 'A few quick questions about your current diaper for a recommendation.',
      storageKey: 'tyk.size.diapers',
      // Result eyebrow line requested by Tykables — frames the rec as
      // experience-based rather than a hard measurement.
      resultEyebrow: 'Based on other customers’ purchases and feedback we recommend:',
      steps: [
        {
          id: 'brand',
          type: 'single',
          label: 'Which brand do you wear the most?',
          help: 'Pick the one you reach for most often.',
          options: [
            { value: 'tykables',     label: 'Tykables' },
            { value: 'nru',          label: 'NRU' },
            { value: 'kiddo',        label: 'Kiddo' },
            { value: 'lngu',         label: 'LNGU' },
            { value: 'pretendagain', label: 'Pretend Again' },
            { value: 'abu',          label: 'ABU' },
            { value: 'northshore',   label: 'NorthShore' },
            { value: 'storebrand',   label: 'Store Brand' },
          ],
        },
        {
          id: 'size',
          type: 'single',
          label: 'What size do you wear in that brand?',
          help: 'If you wear more than one, pick the one you buy most.',
          options: [
            { value: 'small',      label: 'Small' },
            { value: 'medium',     label: 'Medium' },
            { value: 'mediumlarge', label: 'Medium and Large' },
            { value: 'large',      label: 'Large' },
            { value: 'xl',         label: 'XL' },
          ],
          // Some brands don't make a Small — drop it from the choices when
          // the selected brand is one of them. `this` is the step.
          filterOptions: function (answers) {
            var noSmall = { tykables: true, nru: true, pretendagain: true };
            if (noSmall[answers.brand]) {
              return this.options.filter(function (o) { return o.value !== 'small'; });
            }
            return this.options;
          },
        },
        {
          id: 'tapes',
          type: 'single',
          label: 'Where do the tapes land when fastened?',
          help: 'Pick the picture that looks most like where your tabs sit on the front.',
          options: [
            { value: 'sides',   label: 'Out on the sides',   svg: tapeArtSvg(6) },
            { value: 'halfway', label: 'Slightly forward',   svg: tapeArtSvg(22) },
            { value: 'center',  label: 'Near the center',    svg: tapeArtSvg(36) },
          ],
        },
        {
          id: 'legs',
          type: 'single',
          label: 'Is it tight around the legs?',
          help: 'Think about the leg openings, not the waist.',
          options: [
            { value: 'tight', label: 'Yes — tight or leaves marks' },
            { value: 'snug',  label: 'A little snug' },
            { value: 'fine',  label: 'No — fits fine' },
          ],
        },
      ],
      // Tykables currently offers Medium, Large, XL (Regular paused for now).
      sizes: [
        { id: 'Medium', label: 'Medium', chartRange: '28–36' },
        { id: 'Large',  label: 'Large',  chartRange: '36–48' },
        { id: 'XL',     label: 'XL',     chartRange: '48–58' },
      ],
      // Recommendation model:
      //   • Fits run close across brands, so the size you wear carries over
      //     (same-name baseline). Small → Medium (no Small offered),
      //     "Medium and Large" → Large.
      //   • We ONLY size up when the legs are genuinely tight / leave marks.
      //     "A little snug" and "fits fine" keep the baseline size. Tape
      //     position is informational only and never forces a size-up on
      //     its own — tapes out on the sides just corroborates a tight fit;
      //     tapes near the center hint at room to size down.
      //   • ABU runs in several cuts so its size labels don't compare
      //     cleanly; we fall back to the size worn + leg fit and say so.
      evaluate: function (answers) {
        var ladder = ['Medium', 'Large', 'XL'];
        var baseMap = {
          small: 'Medium',
          medium: 'Medium',
          mediumlarge: 'Large',
          large: 'Large',
          xl: 'XL',
        };
        var baseIdx = ladder.indexOf(baseMap[answers.size] || 'Medium');
        if (baseIdx < 0) baseIdx = 0;

        // Only a genuinely tight leg ("Yes — tight or leaves marks") sizes
        // up. "A little snug" and "fits fine" do not.
        var legTight = answers.legs === 'tight';
        var sizeUp = legTight;
        var tapesOut = answers.tapes === 'sides';     // informational only
        var tapesCenter = answers.tapes === 'center'; // informational only

        var recIdx = baseIdx;
        if (sizeUp && recIdx < ladder.length - 1) recIdx++;

        var maxedOut = sizeUp && baseIdx === ladder.length - 1; // wanted up but already XL
        var size = this.sizes.find(function (s) { return s.id === ladder[recIdx]; }) || this.sizes[0];

        // Build a short "why" note (rendered on the result screen).
        var isAbu = answers.brand === 'abu';

        var note;
        if (isAbu) {
          note = 'ABU comes in several different cuts, so its sizes don’t compare directly. ';
          if (maxedOut) {
            note += 'You’re already at our largest size — XL gives the most room at the leg opening.';
          } else if (sizeUp) {
            note += 'Since the legs feel tight, we’ve sized up: a larger diaper gives a higher rise, more room at the legs, and centers the tapes.';
          } else {
            note += 'We went by the size you wear most and your leg fit. If the leg opening ever feels tight or leaves marks, size up.';
          }
        } else if (maxedOut) {
          note = 'You’re already at our largest size (XL). Make sure the tapes are snug for the best leg seal.';
        } else if (sizeUp) {
          note = 'Because the legs feel tight, we suggest going up a size — it gives a higher rise, more room at the leg opening, and the tapes sit more toward the center than the sides.' +
            (tapesOut ? ' The tapes landing out on the sides is another sign your current size runs small.' : '');
        } else if (answers.legs === 'snug') {
          note = 'A little snug at the legs is normal, so your usual size carries over. If it starts to feel tight or leaves marks, size up.';
        } else if (tapesCenter && baseIdx > 0) {
          note = 'That sounds like a roomy, comfortable fit, so your usual size carries over. If you’d prefer a trimmer fit, you could try one size down.';
        } else {
          note = 'Our fit runs close to other brands, so your usual size should carry straight over.';
        }

        // Attach transient display fields to the returned size (recomputed
        // every evaluation; only the diapers quiz uses these objects).
        size.note = note;
        size.eyebrow = this.resultEyebrow;
        return size;
      },
    },

    snappies: {
      id: 'snappies',
      title: 'Find your Snappies size',
      subtitle: 'Think about the t-shirts you buy at Target, Walmart, or similar.',
      storageKey: 'tyk.size.snappies',
      steps: [
        {
          id: 'tshirt',
          type: 'single',
          label: 'What size t-shirt do you usually buy?',
          help: 'Start with whatever fits you at a department store — you can always swap later.',
          options: [
            { value: 'small',  label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large',  label: 'Large' },
            { value: 'xl',     label: 'XL' },
            { value: 'xxxl',   label: '2XL or 3XL' },
          ],
        },
      ],
      sizes: [
        { id: 'Small',  label: 'Small',  chartRange: 'S-size t-shirt' },
        { id: 'Medium', label: 'Medium', chartRange: 'M-size t-shirt' },
        { id: 'Large',  label: 'Large',  chartRange: 'L-size t-shirt' },
        { id: 'XL',     label: 'XL',     chartRange: 'XL-size t-shirt' },
        { id: '2X3X',   label: '2X/3X',  chartRange: '2XL / 3XL t-shirt' },
      ],
      evaluate: function (answers) {
        const map = { small: 'Small', medium: 'Medium', large: 'Large', xl: 'XL', xxxl: '2X3X' };
        const id = map[answers.tshirt] || 'Medium';
        return this.sizes.find(s => s.id === id) || this.sizes[1];
      },
    },

    liddles: {
      id: 'liddles',
      title: 'Find your Liddles size',
      subtitle: 'Our Liddles denim fits like regular jeans — enter your waist.',
      storageKey: 'tyk.size.liddles',
      steps: [
        {
          id: 'waist',
          type: 'numeric',
          label: "What's your waist measurement?",
          help: 'Measure around your natural waist in inches. If you\'re between sizes, size up for comfort.',
          unit: 'in',
          min: 26,
          max: 60,
          default: 34,
        },
      ],
      // Placeholder ranges — confirm with Tykables team, then update both
      // here and the chart table in snippets/tyk-size-chart.liquid.
      sizes: [
        { id: 'Small',  label: 'Small',  chartRange: '28–30', recommendZone: [26, 30] },
        { id: 'Medium', label: 'Medium', chartRange: '31–33', recommendZone: [31, 33] },
        { id: 'Large',  label: 'Large',  chartRange: '34–36', recommendZone: [34, 36] },
        { id: 'XL',     label: 'XL',     chartRange: '37–39', recommendZone: [37, 39] },
        { id: '2X',     label: '2X',     chartRange: '40–42', recommendZone: [40, 42] },
        { id: '3X',     label: '3X',     chartRange: '43–46', recommendZone: [43, 60] },
      ],
      evaluate: function (answers) {
        const w = Number(answers.waist);
        if (!isFinite(w)) return this.sizes[1];
        for (let i = 0; i < this.sizes.length; i++) {
          const [lo, hi] = this.sizes[i].recommendZone;
          if (w >= lo && w <= hi) return this.sizes[i];
        }
        return w < this.sizes[0].recommendZone[0] ? this.sizes[0] : this.sizes[this.sizes.length - 1];
      },
    },

    stepins: {
      id: 'stepins',
      title: 'Find your Step-Ins size',
      subtitle: 'Step-Ins are our pull-on diapers — measured by waist.',
      storageKey: 'tyk.size.stepins',
      steps: [
        {
          id: 'waist',
          type: 'numeric',
          label: "What's your waist measurement?",
          help: 'Measure around your natural waist in inches. Pull-ons need a snug waist to stay up.',
          unit: 'in',
          min: 24,
          max: 60,
          default: 36,
        },
      ],
      // Real ranges — overlap at size boundaries (36, 48) so the chart can
      // show full ranges. recommendZone is the non-overlapping zone the
      // quiz uses to pick a single answer.
      sizes: [
        { id: 'Medium', label: 'Medium', chartRange: '28–36', recommendZone: [28, 35] },
        { id: 'Large',  label: 'Large',  chartRange: '36–48', recommendZone: [36, 47] },
        { id: 'XL',     label: 'XL',     chartRange: '48–56', recommendZone: [48, 56] },
      ],
      evaluate: function (answers) {
        const w = Number(answers.waist);
        if (!isFinite(w)) return this.sizes[1];
        for (let i = 0; i < this.sizes.length; i++) {
          const [lo, hi] = this.sizes[i].recommendZone;
          if (w >= lo && w <= hi) return this.sizes[i];
        }
        return w < this.sizes[0].recommendZone[0] ? this.sizes[0] : this.sizes[this.sizes.length - 1];
      },
    },
  };

  // ── Storage ─────────────────────────────────────────────────────────
  // Storage shape (v2): JSON record { size, answers, ts, synced }.
  // Legacy bare-string entries (v1) are still read via readRecord() and
  // upgraded to the new shape next time they're written.
  //
  //   size    — recommended size id (e.g. "Large")
  //   answers — raw user input (e.g. { waist: 38 }) — used for re-eval
  //   ts      — completion time (ms epoch) — used to resolve cross-device
  //             conflicts against the customer profile metafield
  //   synced  — true once the Flow webhook has confirmed write to profile.
  //             false entries are retried by attemptDeferredSync() on each
  //             page load — this is how a guest's quiz answers land on
  //             their account after they sign up post-quiz.

  function readRecord(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      if (raw.charAt(0) === '{') {
        try {
          var obj = JSON.parse(raw);
          if (obj && typeof obj === 'object' && obj.size) {
            obj.answers = obj.answers || {};
            obj.ts = obj.ts || 0;
            obj.synced = obj.synced === true;
            return obj;
          }
        } catch (e) { /* fall through to legacy */ }
      }
      // Legacy bare-string ("Large") — assume already on profile (it
      // probably came from the seed-from-profile pass in theme.liquid).
      return { size: raw, answers: {}, ts: 0, synced: true };
    } catch (e) { return null; }
  }

  function writeRecord(key, record) {
    try {
      localStorage.setItem(key, JSON.stringify(record));
      document.dispatchEvent(new CustomEvent('tyk:size:saved', {
        detail: { key: key, size: record.size, record: record }
      }));
    } catch (e) { /* private mode / quota — ignore */ }
  }

  // saveSize(key, sizeId, answers?) — accepts the rich form for new
  // callers; falls back to a minimal record so old callers still work.
  function saveSize(key, sizeId, answers) {
    var record = {
      size: sizeId,
      answers: answers || {},
      ts: Date.now(),
      synced: false,
    };
    writeRecord(key, record);
    return record;
  }

  function readSize(key) {
    var rec = readRecord(key);
    return rec ? rec.size : null;
  }

  function clearSize(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  // Optional — POST to a Shopify Flow "HTTP Request received" webhook.
  // When empty, this is a no-op (quiz still works via localStorage).
  // When set, a Flow workflow with two "Update customer metafields"
  // actions writes the result to:
  //   customer.metafields.<namespace>.preferred_size_<quiz>  (text)
  //   customer.metafields.<namespace>.quiz_<quiz>            (JSON)
  //
  // Payload shape (what Flow's trigger step receives):
  //   {
  //     "quiz": "diapers",
  //     "size": "Large",
  //     "namespace": "tykables",
  //     "metafieldKey": "preferred_size_diapers",  // text-field key
  //     "jsonMetafieldKey": "quiz_diapers",         // JSON-field key
  //     "customerId": 1234567890,                   // null if not logged in
  //     "answers": { "waist": 38 },
  //     "ts": 1713974400000,
  //     "completedAt": "2026-04-28T15:00:00.000Z"
  //   }
  //
  // Returns a Promise that resolves to true on 2xx, false otherwise — the
  // caller uses this to mark the localStorage record synced/unsynced so
  // attemptDeferredSync() can retry failures on the next page load.
  //
  // Only runs for logged-in customers — writing a metafield on a null
  // customer has nothing to attach to.
  function syncToProfile(quiz, answers, sizeId, ts) {
    var cfg = window.tykQuizConfig || {};
    var endpoint = cfg.profileSyncUrl || '';
    if (!endpoint) return Promise.resolve(false);
    if (!cfg.customerId) return Promise.resolve(false);

    var namespace = cfg.metafieldNamespace || 'custom';
    var keyPrefix = cfg.metafieldKeyPrefix || 'preferred_size_';
    var completedAt = ts || Date.now();

    var body = {
      quiz: quiz.id,
      size: sizeId,
      namespace: namespace,
      metafieldKey: keyPrefix + quiz.id,
      jsonMetafieldKey: 'quiz_' + quiz.id,
      customerId: cfg.customerId,
      answers: answers,
      ts: completedAt,
      completedAt: new Date(completedAt).toISOString(),
    };
    try {
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Flow webhook URLs are cross-origin; don't send cookies.
        mode: 'cors',
        body: JSON.stringify(body),
      }).then(function (resp) {
        return !!(resp && resp.ok);
      }).catch(function () { return false; });
    } catch (e) { return Promise.resolve(false); }
  }

  // ── Deferred sync ───────────────────────────────────────────────────
  // Walks every tyk.size.* key in localStorage and pushes any record
  // whose synced flag is still false up to the customer profile via the
  // Flow webhook. This is what carries a guest's quiz answers onto their
  // account after they create one — e.g. someone takes the diaper quiz
  // on a PDP, hits Buy, registers at checkout, and lands on the thank-you
  // page; the next page load sees customerId is now set and syncs.
  //
  // Idempotent — if all records are already synced this is a cheap walk
  // and bails out early. Safe to call on every page load.
  function attemptDeferredSync() {
    try {
      var cfg = window.tykQuizConfig || {};
      if (!cfg.customerId || !cfg.profileSyncUrl) return;

      // Collect keys first (don't iterate while mutating).
      var keys = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && k.indexOf('tyk.size.') === 0) keys.push(k);
      }
      if (!keys.length) return;

      keys.forEach(function (key) {
        var rec = readRecord(key);
        if (!rec || !rec.size || rec.synced) return;

        var quizId = key.substring('tyk.size.'.length);
        var quiz = QUIZZES[quizId] || CUSTOM_QUIZZES[quizId] || { id: quizId };

        syncToProfile(quiz, rec.answers || {}, rec.size, rec.ts).then(function (ok) {
          if (!ok) return; // leave synced:false — retried next page load
          // Re-read in case another tab updated the entry meanwhile, then
          // flip the flag without clobbering newer data.
          var fresh = readRecord(key) || rec;
          if (fresh.size === rec.size && fresh.ts === rec.ts) {
            fresh.synced = true;
            writeRecord(key, fresh);
          }
        });
      });
    } catch (e) { /* localStorage unavailable — ignore */ }
  }

  // ── Modal shell ─────────────────────────────────────────────────────
  let openModal = null; // singleton

  function html(tpl) {
    const t = document.createElement('template');
    t.innerHTML = tpl.trim();
    return t.content.firstElementChild;
  }

  function buildModal(quiz, opts) {
    opts = opts || {};
    const wrap = html(`
      <div class="tyk-quiz" role="dialog" aria-modal="true" aria-labelledby="TykQuizTitle" data-tyk-keep-awake>
        <div class="tyk-quiz__scrim" data-tyk-quiz-close tabindex="-1"></div>
        <div class="tyk-quiz__sheet" role="document">
          <div class="tyk-quiz__handle" aria-hidden="true"></div>
          <button type="button" class="tyk-quiz__close" aria-label="Close" data-tyk-quiz-close>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" fill="none"/>
            </svg>
          </button>

          <header class="tyk-quiz__header">
            <h2 id="TykQuizTitle" class="tyk-quiz__title"></h2>
            <p class="tyk-quiz__subtitle"></p>
          </header>

          <div class="tyk-quiz__progress" aria-hidden="true">
            <div class="tyk-quiz__progress-bar"></div>
          </div>

          <div class="tyk-quiz__body" aria-live="polite"></div>

          <footer class="tyk-quiz__footer">
            <button type="button" class="tyk-quiz__btn tyk-quiz__btn--ghost" data-tyk-quiz-back hidden>Back</button>
            <button type="button" class="tyk-quiz__btn tyk-quiz__btn--primary" data-tyk-quiz-next>Next</button>
          </footer>
        </div>
      </div>
    `);

    wrap.querySelector('.tyk-quiz__title').textContent = quiz.title;
    wrap.querySelector('.tyk-quiz__subtitle').textContent = quiz.subtitle || '';

    const state = {
      quiz: quiz,
      stepIndex: 0,
      answers: {},
      opts: opts,
      done: false,
    };

    wrap.__state = state;
    return wrap;
  }

  function renderStep(wrap) {
    const state = wrap.__state;
    const step = state.quiz.steps[state.stepIndex];
    const body = wrap.querySelector('.tyk-quiz__body');
    body.innerHTML = '';
    body.appendChild(renderQuestion(step, state));
    updateProgress(wrap);
    updateNav(wrap);

    // Focus the first interactive element after paint
    requestAnimationFrame(() => {
      const focusable = body.querySelector(
        'input:not([type="hidden"]), button, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        try { focusable.focus({ preventScroll: true }); } catch (e) { focusable.focus(); }
      }
    });
  }

  function renderQuestion(step, state) {
    if (step.type === 'single') return renderSingle(step, state);
    if (step.type === 'multi')  return renderMulti(step, state);
    if (step.type === 'numeric') return renderNumeric(step, state);
    if (step.type === 'text') return renderText(step, state);
    return html(`<p class="tyk-quiz__error">Unsupported question type: ${step.type}</p>`);
  }

  function renderSingle(step, state) {
    const frag = html(`
      <fieldset class="tyk-quiz__field">
        <legend class="tyk-quiz__label"></legend>
        <p class="tyk-quiz__help"></p>
        <div class="tyk-quiz__choices" role="radiogroup"></div>
      </fieldset>
    `);
    frag.querySelector('.tyk-quiz__label').textContent = step.label;
    const help = frag.querySelector('.tyk-quiz__help');
    if (step.help) help.textContent = step.help; else help.remove();

    const choices = frag.querySelector('.tyk-quiz__choices');
    // Options can be filtered by prior answers (e.g. hide "Small" for
    // brands that don't make one). `this`-bound to the step.
    const options = (typeof step.filterOptions === 'function')
      ? step.filterOptions.call(step, state.answers)
      : step.options;
    let saved = state.answers[step.id];
    // If a previously-picked value is no longer offered (brand changed),
    // clear it so the user must re-select rather than carry a stale answer.
    if (saved != null && !options.some(function (o) { return o.value === saved; })) {
      saved = undefined;
      delete state.answers[step.id];
    }
    // Picture choices: when any option carries an svg/image, switch the
    // grid to the media layout (illustration on top, label below).
    const hasMedia = options.some(function (o) { return o.svg || o.image; });
    if (hasMedia) choices.classList.add('tyk-quiz__choices--media');
    options.forEach(function (opt) {
      const id = 'TykQuizOpt-' + step.id + '-' + opt.value;
      const row = html(`
        <label class="tyk-quiz__tile">
          <input type="radio" name="${step.id}" value="${opt.value}" id="${id}">
          <span class="tyk-quiz__tile-label"></span>
        </label>
      `);
      if (opt.svg || opt.image) {
        row.classList.add('tyk-quiz__tile--media');
        const media = document.createElement('span');
        media.className = 'tyk-quiz__tile-media';
        if (opt.svg) {
          media.innerHTML = opt.svg; // trusted, built in-config
        } else {
          const img = document.createElement('img');
          img.src = opt.image;
          img.alt = '';
          img.loading = 'lazy';
          media.appendChild(img);
        }
        row.insertBefore(media, row.querySelector('.tyk-quiz__tile-label'));
      }
      row.querySelector('.tyk-quiz__tile-label').textContent = opt.label;
      const input = row.querySelector('input');
      if (saved === opt.value) input.checked = true;
      input.addEventListener('change', function () {
        state.answers[step.id] = opt.value;
        if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('select');
        // Auto-advance: picking a single-choice option moves to the next
        // step (or the result). Short beat so the selection highlight is
        // visible first. Guarded so it only fires while still on this step
        // (e.g. won't fire if the user navigated away in the meantime).
        if (typeof state.advanceStep === 'function') {
          var idxAtSelect = state.stepIndex;
          window.setTimeout(function () {
            if (state.done) return;
            if (state.stepIndex !== idxAtSelect) return;
            if (state.answers[step.id] !== opt.value) return;
            state.advanceStep();
          }, 200);
        }
      });
      choices.appendChild(row);
    });
    return frag;
  }

  function renderMulti(step, state) {
    const frag = html(`
      <fieldset class="tyk-quiz__field">
        <legend class="tyk-quiz__label"></legend>
        <p class="tyk-quiz__help"></p>
        <div class="tyk-quiz__choices"></div>
      </fieldset>
    `);
    frag.querySelector('.tyk-quiz__label').textContent = step.label;
    const help = frag.querySelector('.tyk-quiz__help');
    if (step.help) help.textContent = step.help; else help.remove();

    const choices = frag.querySelector('.tyk-quiz__choices');
    const saved = Array.isArray(state.answers[step.id]) ? state.answers[step.id] : [];
    step.options.forEach(function (opt) {
      const id = 'TykQuizOpt-' + step.id + '-' + opt.value;
      const row = html(`
        <label class="tyk-quiz__tile">
          <input type="checkbox" name="${step.id}" value="${opt.value}" id="${id}">
          <span class="tyk-quiz__tile-label"></span>
        </label>
      `);
      row.querySelector('.tyk-quiz__tile-label').textContent = opt.label;
      const input = row.querySelector('input');
      if (saved.indexOf(opt.value) !== -1) input.checked = true;
      input.addEventListener('change', function () {
        const current = Array.isArray(state.answers[step.id]) ? state.answers[step.id].slice() : [];
        const idx = current.indexOf(opt.value);
        if (input.checked && idx === -1) current.push(opt.value);
        if (!input.checked && idx !== -1) current.splice(idx, 1);
        state.answers[step.id] = current;
        if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('select');
      });
      choices.appendChild(row);
    });
    return frag;
  }

  function renderNumeric(step, state) {
    const frag = html(`
      <div class="tyk-quiz__field">
        <label class="tyk-quiz__label"></label>
        <p class="tyk-quiz__help"></p>
        <div class="tyk-quiz__num">
          <button type="button" class="tyk-quiz__num-btn" data-nudge="-1" aria-label="Decrease">−</button>
          <input
            type="number"
            class="tyk-quiz__num-input"
            inputmode="numeric"
            enterkeyhint="done"
            step="1"
          >
          <span class="tyk-quiz__num-unit"></span>
          <button type="button" class="tyk-quiz__num-btn" data-nudge="1" aria-label="Increase">+</button>
        </div>
      </div>
    `);
    frag.querySelector('.tyk-quiz__label').textContent = step.label;
    const help = frag.querySelector('.tyk-quiz__help');
    if (step.help) help.textContent = step.help; else help.remove();
    const unit = frag.querySelector('.tyk-quiz__num-unit');
    unit.textContent = step.unit || '';

    const input = frag.querySelector('input');
    if (typeof step.min === 'number') input.min = String(step.min);
    if (typeof step.max === 'number') input.max = String(step.max);
    const value = state.answers[step.id] != null ? state.answers[step.id] : (step.default != null ? step.default : '');
    input.value = value;
    state.answers[step.id] = value === '' ? null : Number(value);

    input.addEventListener('input', function () {
      const n = Number(input.value);
      state.answers[step.id] = isFinite(n) ? n : null;
    });

    frag.querySelectorAll('[data-nudge]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const n = Number(input.value) || (step.default != null ? step.default : 0);
        const delta = Number(btn.getAttribute('data-nudge'));
        let next = n + delta;
        if (typeof step.min === 'number') next = Math.max(step.min, next);
        if (typeof step.max === 'number') next = Math.min(step.max, next);
        input.value = next;
        state.answers[step.id] = next;
        if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('tap');
      });
    });

    return frag;
  }

  function renderText(step, state) {
    const frag = html(`
      <div class="tyk-quiz__field">
        <label class="tyk-quiz__label"></label>
        <p class="tyk-quiz__help"></p>
        <input type="text" class="tyk-quiz__text" enterkeyhint="done">
      </div>
    `);
    frag.querySelector('.tyk-quiz__label').textContent = step.label;
    const help = frag.querySelector('.tyk-quiz__help');
    if (step.help) help.textContent = step.help; else help.remove();
    const input = frag.querySelector('input');
    input.value = state.answers[step.id] || '';
    input.addEventListener('input', function () { state.answers[step.id] = input.value; });
    return frag;
  }

  function renderResult(wrap) {
    const state = wrap.__state;
    const quiz = state.quiz;

    let body;
    let size = null;

    if (quiz.kind === 'feedback') {
      body = html(`
        <div class="tyk-quiz__result">
          <div class="tyk-quiz__result-badge">✓</div>
          <h3 class="tyk-quiz__result-title">Thanks for your feedback</h3>
          <p class="tyk-quiz__result-sub">We'll read every response.</p>
          <div class="tyk-quiz__result-actions">
            <button type="button" class="tyk-quiz__btn tyk-quiz__btn--primary" data-tyk-quiz-close>Done</button>
          </div>
        </div>
      `);
    } else {
      size = typeof quiz.evaluate === 'function' ? quiz.evaluate(state.answers) : null;
      if (size && quiz.storageKey) {
        var record = saveSize(quiz.storageKey, size.id, state.answers);
        // Best-effort sync; mark the record synced on 2xx so the
        // deferred-sync pass on subsequent loads doesn't re-send.
        syncToProfile(quiz, state.answers, size.id, record.ts).then(function (ok) {
          if (!ok) return;
          var fresh = readRecord(quiz.storageKey) || record;
          if (fresh.size === record.size && fresh.ts === record.ts) {
            fresh.synced = true;
            writeRecord(quiz.storageKey, fresh);
          }
        });
      }

      body = html(`
        <div class="tyk-quiz__result">
          <div class="tyk-quiz__result-eyebrow">Your recommended size</div>
          <div class="tyk-quiz__result-size"></div>
          <p class="tyk-quiz__result-sub"></p>
          <div class="tyk-quiz__result-actions">
            <button type="button" class="tyk-quiz__btn tyk-quiz__btn--ghost" data-tyk-quiz-retake>Retake</button>
            <button type="button" class="tyk-quiz__btn tyk-quiz__btn--primary" data-tyk-quiz-use>Use this size</button>
          </div>
        </div>
      `);
      body.querySelector('.tyk-quiz__result-size').textContent = size ? size.label : '—';

      // Optional custom eyebrow (e.g. diapers: "Based on other customers'
      // purchases and feedback we recommend:"). Falls back to the default.
      var eyebrowEl = body.querySelector('.tyk-quiz__result-eyebrow');
      if (eyebrowEl && size && (size.eyebrow || quiz.resultEyebrow)) {
        eyebrowEl.textContent = size.eyebrow || quiz.resultEyebrow;
      }

      // Sub line: lead with the quiz's "why" note when present, then the
      // pre-select reassurance.
      var subParts = [];
      if (size && size.note) subParts.push(size.note);
      if (size) subParts.push('We\'ll pre-select ' + size.label + ' on product pages.');
      body.querySelector('.tyk-quiz__result-sub').textContent = subParts.join(' ');

      if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('success');
    }

    const bodyEl = wrap.querySelector('.tyk-quiz__body');
    bodyEl.innerHTML = '';
    bodyEl.appendChild(body);

    // Hide the nav footer on the result screen — the result has its own
    // action buttons wired via delegated click below.
    wrap.querySelector('.tyk-quiz__footer').hidden = true;
    wrap.querySelector('.tyk-quiz__progress-bar').style.transform = 'scaleX(1)';

    state.done = true;
    state.resultSize = size;
  }

  function updateProgress(wrap) {
    const state = wrap.__state;
    const pct = state.quiz.steps.length ? (state.stepIndex / state.quiz.steps.length) : 0;
    wrap.querySelector('.tyk-quiz__progress-bar').style.transform = 'scaleX(' + pct + ')';
  }

  function updateNav(wrap) {
    const state = wrap.__state;
    const back = wrap.querySelector('[data-tyk-quiz-back]');
    const next = wrap.querySelector('[data-tyk-quiz-next]');
    back.hidden = state.stepIndex === 0;
    next.textContent = (state.stepIndex === state.quiz.steps.length - 1) ? 'See result' : 'Next';
  }

  function attachControls(wrap) {
    const state = wrap.__state;

    function close() {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      document.body.classList.remove('tyk-quiz-open');
      openModal = null;
      if (state.opts.onClose) state.opts.onClose(state);
      if (window.tykNative && window.tykNative.restoreFocus) window.tykNative.restoreFocus(wrap);
    }

    function next() {
      if (state.done) return;
      // Basic required-field validation
      const step = state.quiz.steps[state.stepIndex];
      const a = state.answers[step.id];
      if (a == null || a === '' || (Array.isArray(a) && a.length === 0)) {
        const body = wrap.querySelector('.tyk-quiz__body');
        body.classList.remove('tyk-quiz__body--shake');
        void body.offsetWidth; // restart animation
        body.classList.add('tyk-quiz__body--shake');
        if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('warning');
        return;
      }

      if (state.stepIndex < state.quiz.steps.length - 1) {
        state.stepIndex++;
        renderStep(wrap);
      } else {
        renderResult(wrap);
      }
    }

    // Exposed so single-choice tiles can auto-advance on selection.
    state.advanceStep = next;

    function back() {
      if (state.done) {
        state.done = false;
        wrap.querySelector('.tyk-quiz__footer').hidden = false;
      }
      if (state.stepIndex > 0) state.stepIndex--;
      renderStep(wrap);
    }

    function retake() {
      state.done = false;
      state.stepIndex = 0;
      state.answers = {};
      wrap.querySelector('.tyk-quiz__footer').hidden = false;
      renderStep(wrap);
    }

    function useSize() {
      const size = state.resultSize;
      if (size && state.opts.onResult) state.opts.onResult(size, state);
      close();
    }

    wrap.addEventListener('click', function (e) {
      const t = e.target;
      if (t.closest('[data-tyk-quiz-close]')) return close();
      if (t.closest('[data-tyk-quiz-next]'))  return next();
      if (t.closest('[data-tyk-quiz-back]'))  return back();
      if (t.closest('[data-tyk-quiz-retake]')) return retake();
      if (t.closest('[data-tyk-quiz-use]'))   return useSize();
    });

    wrap.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key === 'Enter' && !state.done) {
        const inField = e.target.closest('.tyk-quiz__num, .tyk-quiz__text, .tyk-quiz__choices');
        if (inField) {
          e.preventDefault();
          next();
        }
      }
    });

    // Click outside sheet = close
    wrap.querySelector('.tyk-quiz__scrim').addEventListener('click', close);
  }

  // ── Public open ─────────────────────────────────────────────────────
  function open(quizId, opts) {
    // Admin-authored (metaobject) configs take precedence over the JS
    // built-ins so merchants can override 'diapers'/'snappies'/etc. by
    // creating an entry with the same id in admin without a code change.
    const quiz = CUSTOM_QUIZZES[quizId] || QUIZZES[quizId];
    if (!quiz) {
      if (window.console) console.warn('[tyk-quiz] unknown quiz:', quizId);
      return;
    }
    if (openModal) {
      try { openModal.remove(); } catch (e) {}
      openModal = null;
    }

    const wrap = buildModal(quiz, opts || {});
    document.body.appendChild(wrap);
    document.body.classList.add('tyk-quiz-open');
    openModal = wrap;

    attachControls(wrap);
    // Delay a tick so the open animation plays
    requestAnimationFrame(() => wrap.classList.add('is-open'));
    renderStep(wrap);

    // Haptic on open
    if (window.tykNative && window.tykNative.haptic) window.tykNative.haptic('tap');
  }

  // Custom quizzes registered at runtime — populated by:
  //   • snippets/tyk-quiz-config.liquid (metaobject-driven definitions)
  //   • the legacy tyk-quiz-config <element> custom-element entrypoint
  //   • anyone calling window.tykQuiz.register(...) directly
  //
  // Metaobject-driven configs arrive as pure data (no JS functions). For
  // 'size' kind quizzes we synthesize an evaluate() from the step type +
  // size ranges so the runtime treats them identically to built-ins.
  const CUSTOM_QUIZZES = {};

  function buildSizeEvaluator(cfg) {
    return function (answers) {
      var sizes = cfg.sizes || [];
      if (!sizes.length) return null;

      // Find the first numeric or single-choice step — that's the
      // signal that drives the recommendation. Multi-step quizzes
      // can override by providing their own evaluate.
      var driver = (cfg.steps || []).find(function (s) {
        return s.type === 'numeric' || s.type === 'single';
      });
      if (!driver) return sizes[0];

      // Numeric: pick the size whose recommend zone covers the value.
      if (driver.type === 'numeric') {
        var v = Number(answers[driver.id]);
        if (!isFinite(v)) return sizes[Math.floor(sizes.length / 2)];
        for (var i = 0; i < sizes.length; i++) {
          var lo = pickLow(sizes[i]);
          var hi = pickHigh(sizes[i]);
          if (lo == null || hi == null) continue;
          if (v >= lo && v <= hi) return sizes[i];
        }
        // Out of range → clamp to the nearest end.
        var firstLo = pickLow(sizes[0]);
        return (firstLo != null && v < firstLo) ? sizes[0] : sizes[sizes.length - 1];
      }

      // Single-choice: each option carries a mapsToSizeId pointing at
      // one of the configured sizes.
      if (driver.type === 'single') {
        var picked = answers[driver.id];
        var opt = (driver.options || []).find(function (o) { return o.value === picked; });
        if (!opt || !opt.mapsToSizeId) return sizes[Math.floor(sizes.length / 2)];
        return sizes.find(function (s) { return s.id === opt.mapsToSizeId; })
          || sizes[Math.floor(sizes.length / 2)];
      }

      return sizes[0];
    };

    function pickLow(s) {
      if (s.recommendZone && s.recommendZone.length === 2) return Number(s.recommendZone[0]);
      if (s.recommendMin != null) return Number(s.recommendMin);
      return null;
    }
    function pickHigh(s) {
      if (s.recommendZone && s.recommendZone.length === 2) return Number(s.recommendZone[1]);
      if (s.recommendMax != null) return Number(s.recommendMax);
      return null;
    }
  }

  function register(cfg) {
    if (!cfg || !cfg.id) return;
    var kind = cfg.kind || 'size';

    // Default storage key for size quizzes — keeps the localStorage key
    // shape consistent (tyk.size.<id>) so the deferred-sync pass picks
    // them up automatically.
    if (kind === 'size' && !cfg.storageKey) {
      cfg.storageKey = 'tyk.size.' + cfg.id;
    }

    // Synthesize evaluate() from data when caller didn't supply one.
    if (kind === 'size' && typeof cfg.evaluate !== 'function') {
      cfg.evaluate = buildSizeEvaluator(cfg);
    }

    cfg.kind = kind;
    CUSTOM_QUIZZES[cfg.id] = cfg;
  }

  // ── Delegated trigger: any element with [data-tyk-quiz="<id>"] ──────
  document.addEventListener('click', function (e) {
    const trigger = e.target && e.target.closest && e.target.closest('[data-tyk-quiz]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-tyk-quiz');
    if (!id) return;
    e.preventDefault();

    // onResult — if the trigger is inside a PDP, try to pre-select the
    // matching variant via the size auto-selector.
    const opts = {};
    if (window.tykSizeAutoSelect && window.tykSizeAutoSelect.applyToNearestForm) {
      opts.onResult = function (size) {
        window.tykSizeAutoSelect.applyToNearestForm(trigger, size.id);
      };
    }
    open(id, opts);
  });

  // ── Public API ──────────────────────────────────────────────────────
  window.tykQuiz = {
    open: open,
    register: register,
    getSize: function (key) { return readSize(key); },
    getRecord: function (key) { return readRecord(key); },
    clear: function (key) { clearSize(key); },
    syncNow: attemptDeferredSync,
    quizzes: QUIZZES,
    customQuizzes: CUSTOM_QUIZZES,
  };

  // ── Page-load: push any unsynced quiz answers onto the customer ─────
  // This is the guest→account bridge: when a guest takes the quiz then
  // creates an account at checkout, their localStorage record is still
  // synced:false. The next page they land on (thank-you, account, home,
  // anywhere) sees customerId is now set, fires the webhook, flips the
  // synced flag, and the metafield is on their profile from then on.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attemptDeferredSync);
  } else {
    // Defer one tick so theme.liquid's seed-from-profile script and
    // tykQuizConfig population run first.
    setTimeout(attemptDeferredSync, 0);
  }
})();
